import { HiikerMapStyle } from "../../../../utils/models/MapStyles";
import { Module, ActionContext } from "vuex"; // Updated Vuex imports
import { OnBoardingGoal } from "../../../../utils/models/OnBoardingGoal";
import { RevenueCatWrapper } from "../../../../utils/RevenueCatWrapper";
import { authReady } from "../../../../utils/firebase/authReady";

// Define the shape of the state using an interface or class
// Using the class here is fine for defining the structure
export class BaseMapStylesState {
  currentMapStyle: HiikerMapStyle | null = null;
  printMapMode: boolean = false;
}

// Define the type for the Root State if you have one, otherwise use 'any'
// Replace 'any' with your actual RootState interface/type if possible
type RootState = any;

// --- State ---
// State should be a function that returns the initial state object in Vuex 4 modules
const state = (): BaseMapStylesState => ({
  currentMapStyle: null, // Initial value set here
  printMapMode: false,  // Initial value set here
});

// --- Actions ---
// ActionTree<MapStylesState, RootState> is still valid,
// or you can type context more specifically using ActionContext
const actions = {
  async initialiseStore(context: ActionContext<BaseMapStylesState, RootState>) {
    // No need to await dispatch if setMapStyleFromQueryParams doesn't return a promise
    // related to its completion that initialiseStore needs to wait for.
    // If it just triggers background tasks, you might not need await here.
    // However, if subsequent logic in initialiseStore depends on query params being processed, keep the await.
    await context.dispatch('setMapStyleFromQueryParams');

    const isProOrProPlus = await RevenueCatWrapper.isProOrProPlus();
    // Use commit to mutate state
    context.commit('setCurrentMapStyle', HiikerMapStyle.getCurrentStyle(isProOrProPlus));
  },

  async setMapStyleFromQueryParams(context: ActionContext<BaseMapStylesState, RootState>) {
    const params = new URLSearchParams(window.location.search);
    if (params.has('mapStyle')) {
      const value = params.get('mapStyle');
      if (!value) return; // Added safety check

      let mapStyle = HiikerMapStyle.fromString(value);
      if (!mapStyle) return; // Added safety check if fromString can return null/undefined

      // Logic for non-pro styles
      if (!mapStyle.isPro) {
        mapStyle.saveCurrentStyle();
        setTimeout(() => {
          // Check window.map safely
          if (window.map && typeof window.map.setStyle === 'function') {
            window.map.setStyle(mapStyle.styleUrl);
          }
        }, 1500);
        return;
      }

      // Logic for pro styles
      await authReady; // Wait for Firebase auth sync to complete (sets window.user)
      const isProOrProPlus = await RevenueCatWrapper.isProOrProPlus();

      // User not logged in
      // Ensure window.user and window.onBoardingVue exist and have the expected methods
      if (window.user == null) {
        setTimeout(() => {
          if (window.onBoardingVue && typeof window.onBoardingVue.launchFor === 'function') {
            window.onBoardingVue.launchFor(OnBoardingGoal.MapStyles);
          }
        }, 1500);
        return;
      }

      // User is logged in but doesn't have Pro trying to access Pro style
      if (mapStyle.isPro && !isProOrProPlus) {
        setTimeout(() => {
          window.location.href = '/pricing'; // Consider using Vue Router for navigation if applicable
        }, 1500);
      }
      // User has Pro or style is not Pro (redundant check here due to earlier !mapStyle.isPro check, but safe)
      else {
        mapStyle.saveCurrentStyle();
        setTimeout(() => {
          // Check window.map safely
          if (window.map && typeof window.map.setStyle === 'function') {
            window.map.setStyle(mapStyle.styleUrl);
          }
        }, 1500);
        // Optionally commit the style change to the store state as well, if needed elsewhere
        // context.commit('setCurrentMapStyle', mapStyle);
      }
    } else {
      // If no mapStyle query param, maybe initialize with default?
      // Or ensure initialiseStore handles the default case correctly
      // Currently, initialiseStore fetches the default *after* this action potentially runs
    }
  },
};

// --- Mutations ---
const mutations = {
  setCurrentMapStyle(state: BaseMapStylesState, mapStyle: HiikerMapStyle | null) { // Allow null for initialization
    state.currentMapStyle = mapStyle;
  },
  setPrintMapMode(state: BaseMapStylesState, printMapMode: boolean) {
    state.printMapMode = printMapMode;
  },
};

// --- Getters ---
const getters = {
  // Define getters here if needed, e.g.:
  // getCurrentStyle: (state: MapStylesState) => state.currentMapStyle,
  // isPrintMode: (state: MapStylesState) => state.printMapMode,
};

// --- Module Definition ---
// Export a plain object conforming to the Module interface
const baseMapStylesModule: Module<BaseMapStylesState, RootState> = {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
  // modules: {} // No nested modules in this case
};

export default baseMapStylesModule;
